import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";

import Sidebar from "./components/Sidebar";
import ChatWindow from "./components/ChatWindow";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import AdminLogin from "./pages/admin/AdminLogin";
import Subscription from "./pages/Subscription";
import PaymentCallback from "./pages/PaymentCallback";
import SharedResponsePage from "./pages/SharedResponsePage";

import AdminLayout from "./layout/AdminLayout";
import UserLayout from "./layout/UserLayout";
import PageLayout from "./layout/PageLayout";

import TermsAndConditions from "./pages/TermsAndConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ReturnPolicy from "./pages/ReturnPolicy";
import ContactSupport from "./pages/ContactSupport";
import Footer from "./components/Footer";

import AdminDashboard from "./pages/admin/AdminDashboard";
import Users from "./pages/admin/Users";
import Plans from "./pages/admin/Plans";
import UserDetails from "./pages/admin/UserDetails";
import AddPlan from "./pages/admin/AddPlan";

import { setUser, setCredits } from "./store/userSlice";
import { setActiveChatId, setSidebarCollapsed } from "./store/chatSlice";

import API from "./api/api";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminRoute from "./routes/AdminRoute";
import MyAccount from "./pages/MyAccount";
import AddDepartment from "./pages/admin/AddDepartment";
import Departments from "./pages/admin/Departments";
import EditAccount from "./pages/EditAccount";
import States from "./pages/admin/States";
import AddState from "./pages/admin/AddState";

import Settings from "./pages/admin/Settings";
import Payments from "./pages/admin/Payments";
import Credits from "./pages/admin/Credits";
import Usage from "./pages/admin/Usage";

// ── Auth guard — redirect logged-in users away from login/signup ──
function AuthRoute({ children }) {
  const user = useSelector((state) => state.user.user);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isAddAccount = searchParams.get("mode") === "add_account";

  if (user && !isAddAccount) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/"} replace />;
  }
  return children;
}

// ── Home / Chat layout ──
function HomeLayout() {
  const sidebarCollapsed = useSelector((state) => state.chat.sidebarCollapsed);
  const dispatch = useDispatch();

  return (
    <div className="layout-wrapper">
      <Sidebar />
      {!sidebarCollapsed && (
        <div
          className="mobile-sidebar-overlay"
          onClick={() => dispatch(setSidebarCollapsed(true))}
        />
      )}
      <div className="main-content-with-footer">
        <div className="main-content">
          <ChatWindow />
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const dispatch = useDispatch();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // ── Safety timeout: if backend doesn't respond in 4s, proceed as guest ──
    const timer = setTimeout(() => {
      setAuthChecked(true);
    }, 4000);

    API.get("/auth/me", { withCredentials: true })
      .then((res) => {
        if (res?.data?.data) {
          dispatch(setUser(res.data.data));
          dispatch(setCredits(res.data.data.credits ?? 10));
        }
      })
      .catch(() => {
        dispatch(setUser(null));
      })
      .finally(() => {
        clearTimeout(timer);
        setAuthChecked(true);
      });

    return () => clearTimeout(timer);
  }, [dispatch]);
  useEffect(() => {
    const savedChatId = localStorage.getItem("activeChatId");

    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith("chat_messages_temp-")) {
        localStorage.removeItem(k);
      }
    });

    if (savedChatId && savedChatId.startsWith("temp-")) {
      localStorage.removeItem("activeChatId");
    }

    if (savedChatId && !savedChatId.startsWith("temp-")) {
      dispatch(setActiveChatId(savedChatId));
    }
  }, [dispatch]);

  useEffect(() => {
    if (!localStorage.getItem("grgpt_guest_id")) {
      localStorage.setItem("grgpt_guest_id", uuidv4());
    }
  }, []);

  return (
    <div className="app-wrapper">
      {/* ── Main Website Container ── */}
      <div className="app-main-view app-main-view--ready">
        <Routes>

          <Route path="/" element={<HomeLayout />} />
          <Route path="/share/:shareId" element={<SharedResponsePage />} />

        <Route element={<PageLayout />}>
          <Route
            path="/login"
            element={
              <AuthRoute>
                <Login />
              </AuthRoute>
            }
          />
          <Route path="/signup" element={<Signup />} />
          <Route path="/subscription" element={<Subscription />} />
          <Route path="/payment/status" element={<PaymentCallback />} />
          <Route path="/payment/callback" element={<PaymentCallback />} />
          <Route path="/terms" element={<TermsAndConditions />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/contact" element={<ContactSupport />} />
          <Route path="/contact-support" element={<ContactSupport />} />
        </Route>

        <Route element={<ProtectedRoute />}>
          <Route element={<UserLayout />}>
            <Route path="/account" element={<MyAccount />} />
            <Route path="/edit-account" element={<EditAccount />} />
          </Route>
        </Route>

        <Route
          path="/admin/login"
          element={
            <AuthRoute>
              <AdminLogin />
            </AuthRoute>
          }
        />


        <Route element={<AdminRoute authChecked={authChecked} />}>
          <Route path="/admin" element={<AdminLayout />}>

            {/* DASHBOARD */}
            <Route index element={<AdminDashboard />} />

            {/* USERS */}
            <Route path="users" element={<Users />} />
            <Route path="users/:userId" element={<UserDetails />} />

            {/* PLANS */}
            <Route path="plans" element={<Plans />} />
            <Route path="plans/add" element={<AddPlan />} />

            {/* DEPARTMENTS */}
            <Route path="departments" element={<Departments />} />
            <Route path="add-department" element={<AddDepartment />} />

            {/* STATES */}
            <Route path="states" element={<States />} />
            <Route path="states/add" element={<AddState />} />

            {/* TRANSACTIONS */}
            <Route path="payments" element={<Payments />} />
            <Route path="credits" element={<Credits />} />
            <Route path="usage" element={<Usage />} />

            {/* SETTINGS */}
            <Route path="settings" element={<Settings />} />

          </Route>
        </Route>

        <Route path="/chat" element={<Navigate to="/" replace />} />

        <Route
          path="*"
          element={
            <div className="centered">
              <h2>Page not found</h2>
              <Link to="/">Go Home</Link>
            </div>
          }
        />

        </Routes>
      </div>
    </div>
  );
}